import React, { useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { IFlowTemplate } from '~/types/model.types';
import { arraySorter } from '~/utils/sort.utils';
import { useRootStore } from '~/stores/useRootStore';

import Shimmer from '~/components/common/FlowTemplates/Shimmer/Shimmer';
import FlowActions from '~/components/common/FlowTemplates/FlowCard/FlowActions';
import FlowThumbnailWithPopover from '~/components/common/FlowThumbnailWithPopover/FlowThumbnailWithPopover';
import SelectedTemplateWarningModal from '~/components/Automations/AutomationsBuilder/AutomationsBuilderSideBar/Components/SelectedTemplateWarningModal';
import { TemplatesList } from '../StepSideBar/StepSideBar.styles';
import TemplateSelectEmptyState from './TemplateSelectEmptyState';
import TemplatesListItemWrapper from '../Components/TemplatesListItemWrapper';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';

interface Props {
    onClose: () => void;
}

const TemplateSelectSideBarFlow = ({ onClose }: Props) => {
    const {
        automationCtrl,
        automationUiStore: {
            selectedStep,
            wasTemplateWarningModalShown,
            setWasTemplateWarningModalShown
        },
        templatesUiStore: {
            flowTemplates,
            panelFlowTemplates,
            requestStatus,
            activeFlowPanel
        },
        flowCtrl
    } = useRootStore();
    const {
        userStore: { currUser }
    } = useGlobalMX();

    const userType = currUser.flowsBetaUserType;

    const sortedFlows = arraySorter(
        userType.hasNewFlowExp ? panelFlowTemplates : flowTemplates
    );

    const isSelected = (id: string) => id === selectedStep?.fileTemplateId;

    const [selectedTemplate, setSelectedTemplate] = useState<IFlowTemplate>();

    const shouldShowWarningModal =
        !wasTemplateWarningModalShown && !!selectedTemplate;

    const closeModal = () => {
        setWasTemplateWarningModalShown(true);
        setSelectedTemplate(null);
    };

    const handleTemplateSelect = (template: IFlowTemplate) => {
        const hasContract =
            template.action_block_types.includes('contract_block');
        const hasScheduler = template.action_block_types.includes(
            'schedule_container_block'
        );
        if ((hasContract || hasScheduler) && !wasTemplateWarningModalShown) {
            setSelectedTemplate(template);
        } else {
            onUseTemplateClick(template);
        }
    };

    if (requestStatus === 'fetching') {
        return (
            <TemplatesList>
                <Shimmer shouldShowRows count={4} rowsHeight={63} />
            </TemplatesList>
        );
    }

    const onEditTemplateClick = () => {
        closeModal();
    };

    const onUseTemplateClick = (template: IFlowTemplate) => {
        automationCtrl.selectFlowTemplate(template._id);
        onClose();
        if (template.require_authentication === false) {
            flowCtrl.showAuthenticationToast(ToastType.Error, () =>
                flowCtrl.configureFlowInNewTab(template._id)
            );
        }
        if (shouldShowWarningModal) {
            closeModal();
        }
    };

    return (
        <>
            <TemplatesList>
                {sortedFlows?.length ? (
                    sortedFlows.map(template => (
                        <Wrapper
                            key={template._id}
                            onClick={() => handleTemplateSelect(template)}
                            isSelected={isSelected(template._id)}
                        >
                            <Container>
                                <FlowThumbnailWithPopover
                                    thumbnailImage={template.thumbnail_image}
                                    flowTitle={template.title}
                                    hasPendingChanges={
                                        template.has_pending_changes
                                    }
                                    popoverProps={{
                                        placement: 'left',
                                        offsetY: 55,
                                        offsetX: 10
                                    }}
                                />
                                <InnerContainer>
                                    <TemplateTitle
                                        color="neutral900"
                                        ellipsis
                                        tooltipProps={{
                                            disabled: false,
                                            delayShowInMs: 500
                                        }}
                                        type="emphasis16"
                                    >
                                        {template.title}
                                    </TemplateTitle>
                                    <HBText
                                        type="body14"
                                        color="neutral700"
                                        transform="capitalize"
                                    >
                                        <FlowActions
                                            actions={
                                                template.action_block_types
                                            }
                                            maxVisibleActions={2}
                                        />
                                    </HBText>
                                </InnerContainer>
                            </Container>
                        </Wrapper>
                    ))
                ) : (
                    <TemplateSelectEmptyState
                        type="smart file"
                        testIdName="flow-empty-state"
                        activeFlowPanel={activeFlowPanel}
                    />
                )}
            </TemplatesList>
            <SelectedTemplateWarningModal
                isOpen={shouldShowWarningModal}
                onEditClick={onEditTemplateClick}
                onUseClick={onUseTemplateClick}
                selectedTemplate={selectedTemplate}
            />
        </>
    );
};

export default observer(TemplateSelectSideBarFlow);

const Wrapper = styled(TemplatesListItemWrapper)`
    height: 66px;
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const InnerContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const TemplateTitle = styled(HBText)`
    max-width: 200px;
`;
